import {FileUpload, Loader, Modal} from '@/components/atoms'
import {convertImageToWebP} from '@/helpers/shadcn/utils'
import {useImagesUpload} from '@/hooks/useImagesUpload'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {Constants, FILE} from '@/utils/constants/commonConstants'
import {MODAL_TYPE} from '@/utils/constants/globalStyle'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import {useFeatureValue} from '@growthbook/growthbook-react'
import {useState} from 'react'

const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])
const MAX_CONCURRENT_UPLOADS = 5

const UploadMediaModal = ({openUpload, setOpenUpload, entityType = 'product', title = 'Upload Media', ...props}) => {
  const {uploading, setUploadErrors, onUploadImages, uploadErrors, progress, setIsSingleImgUpload, setAllProdIds} =
    useImagesUpload()
  const [images, setImages] = useState([])
  const [convertedImgs, setConvertedImages] = useState([])
  const dispatch = useAppDispatch()

  const [isLoading, setLoader] = useState(false)
  const {
    imageCountAllowed = 50,
    maxCollectionOfImageSize = '25MB',
    maxIndividualImageSize = '2MB',
  } = useFeatureValue(growthBookFeatureFlags.AdminBffImageValidationRules) || {}
  const maxImageCollectionSize = entityType === 'product' ? maxCollectionOfImageSize : '50MB'
  const maxSizeInMB = Number(maxImageCollectionSize?.match(/\d+/)?.[0])
  const maxAllowedImageCount = entityType === 'product' ? imageCountAllowed : 50

  const isCloseModal = () => {
    setOpenUpload(false)
    setImages([])
    setUploadErrors([])
    setConvertedImages([])
    setLoader(false)
    setIsSingleImgUpload(false)
    setAllProdIds([])
  }

  const handleDelete = fileIndex => {
    setImages(prevImages => {
      const updatedImages =
        fileIndex === -1 ? prevImages.slice(1) : prevImages.filter((_, index) => index !== fileIndex)

      if (individualImageValidation(updatedImages) && imageValidation(updatedImages)) {
        setUploadErrors([])
      }

      return updatedImages
    })

    setConvertedImages(prevConverted =>
      fileIndex === -1 ? prevConverted.slice(1) : prevConverted.filter((_, index) => index !== fileIndex)
    )
  }

  const onFilesAdded = async event => {
    const allFiles = Array.from(event.target?.files || event.dataTransfer?.files || [])

    const allowedImagesFormat = allFiles.filter(file => ALLOWED_IMAGE_TYPES.has(file.type))
    if (allowedImagesFormat?.length === 0) {
      return
    }

    const selectedFileNames = new Set(images.map(image => image.name))
    const newImages = allowedImagesFormat.filter(file => !selectedFileNames.has(file.name))

    if (newImages?.length === 0) {
      return
    }

    const updatedImages = [...images, ...newImages]
    imageValidation(updatedImages)
    individualImageValidation(updatedImages)

    setLoader(true)

    try {
      const convertedFiles = await Promise.all(newImages.map(file => convertImageToWebP(file, {entityType})))
      setConvertedFiles(convertedFiles)
    } finally {
      setLoader(false)
    }
  }

  const individualImageValidation = updatedArray => {
    if (!Array.isArray(updatedArray) || updatedArray.length === 0) return true

    const fileNames = []
    let hasError = false

    const maxSizeBytes = Number(maxIndividualImageSize?.match(/\d+/)?.[0]) * FILE.SIZE_BYTES

    for (const file of updatedArray) {
      file.error = false

      if (file?.size > maxSizeBytes) {
        file.error = true
        fileNames.push(file.name)
        hasError = true
      }
    }

    if (hasError) {
      const errorConst = fileNames.length > 1 ? 'these files are' : 'this file is'
      const errMsg =
        updatedArray.length === 1
          ? 'Each image must be less than or equal to 2 MB.'
          : `${fileNames.join(', ')} ${errorConst} exceeding the 2 MB individual file size limit.`

      setUploadErrors([errMsg])
      return false
    }

    return true
  }

  const imageValidation = updatedArray => {
    if (!Array.isArray(updatedArray) || updatedArray.length === 0) return true

    const maxAllowedSize = maxSizeInMB * FILE.SIZE_BYTES
    const totalSize = updatedArray.reduce((sum, file) => sum + (file?.size || 0), 0)

    if (updatedArray.length > maxAllowedImageCount) {
      setUploadErrors([`You can not upload more than ${maxAllowedImageCount} files. Remove some files to continue.`])
      return false
    }

    if (totalSize >= maxAllowedSize) {
      setUploadErrors([`Total image size must not exceed ${maxSizeInMB} MB.`])
      return false
    }

    return true
  }

  const setConvertedFiles = convertedFiles => {
    if (Array.isArray(convertedFiles) && convertedFiles.length > 0) {
      setImages(prev => [...prev, ...convertedFiles.map(({file}) => file)])
      setConvertedImages(prev => [...prev, ...convertedFiles.map(({metadata, webpFile}) => ({metadata, webpFile}))])
    }
  }

  const handleUpload = async () => {
    setUploadErrors([])
    setAllProdIds([])
    setIsSingleImgUpload(false)

    if (!convertedImgs?.length) return

    const uploadOne = ({webpFile, metadata}) => {
      const formData = new FormData()
      formData.append('images', webpFile)
      const modifiedMetadata = {...metadata, referenceEntity: entityType}
      formData.append('metadata', JSON.stringify({mediaUploadMetadata: [modifiedMetadata]}))

      return onUploadImages(formData)
    }

    const uploadWithConcurrencyLimit = async items => {
      const results = new Array(items.length)
      const queue = items.map((item, index) => ({item, index}))

      const worker = async () => {
        while (queue.length > 0) {
          const nextUpload = queue.shift()

          if (!nextUpload) {
            return
          }

          const {item, index} = nextUpload

          try {
            results[index] = {status: 'fulfilled', value: await uploadOne(item)}
          } catch (reason) {
            results[index] = {status: 'rejected', reason}
          }
        }
      }

      await Promise.all(Array.from({length: Math.min(MAX_CONCURRENT_UPLOADS, items.length)}, () => worker()))
      return results
    }

    try {
      const results = await uploadWithConcurrencyLimit(convertedImgs)
      const failedCount = results.filter(result => result.status === 'rejected').length

      if (failedCount === 0) {
        isCloseModal()
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Images uploaded successfully',
            type: 'success',
          })
        )
      } else {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Some images failed to upload. Please review the errors and retry.',
            type: 'warning',
          })
        )
      }
    } catch (error) {
      console.error('Unexpected error during uploads:', error)
    }
  }

  const files = images.map(file => file.name)
  const hasErrors = uploadErrors?.length > 0

  const renderErrors = () => {
    if (hasErrors) {
      return (
        <div>
          {uploadErrors.map(error => (
            <div key={error}>{error}</div>
          ))}
        </div>
      )
    }
    return null
  }

  const imageUri = images?.length === 1 && !hasErrors ? URL?.createObjectURL(images[0]) : ''

  return (
    <Modal
      isDark={true}
      onCloseModal={isCloseModal}
      onClickSecondaryButton={isCloseModal}
      onClickPrimaryButton={handleUpload}
      showModal={openUpload}
      className="upload-target-modal"
      title={title}
      primaryButtonText={'Done'}
      primaryButtonDisabled={uploading || hasErrors || images.length <= 0 || images.length > maxAllowedImageCount}
      type={MODAL_TYPE.SMALL}
      showSecondaryButton={true}
      secondaryButtonProps={{
        disabled: uploading,
      }}
      secondaryButtonText={Constants.Cancel}
      data-testId="upload-images-modal"
      showTertiaryButton={false}
      onClickTertiaryButton={isCloseModal}
      tertiaryButtonText="Cancel"
      content={
        isLoading ? (
          <Loader />
        ) : (
          <FileUpload
            acceptedExtensions=".jpg,.png,.webp"
            description="Drag and drop your file here (only .jpg, .png, .webp)"
            label={`Upload media files (The maximum combined size of all uploaded images must not exceed ${maxImageCollectionSize}.)`}
            variant={uploading ? Constants.Uploading : Constants.Edit}
            buttonLabel={Constants.UploadFile}
            onFilesAdded={onFilesAdded}
            onUploadDelete={handleDelete}
            type="image"
            required={true}
            files={files}
            multiple={true}
            value={files}
            isDark={true}
            feedbackType={hasErrors ? Constants.Error : Constants.Info}
            helperText={renderErrors()}
            onUploadError={error => {
              setUploadErrors([error])
            }}
            progressPercentage={progress}
            imageUri={imageUri}
            dataTestId="upload-images"
            {...props}
          />
        )
      }
    />
  )
}

export default UploadMediaModal
